/**
 * Verify Email Page
 *
 * Handles email verification links by calling the backend API
 * and showing a clear success or error state.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, ErrorMessage, SuccessMessage } from '../components/AuthForm';

type VerifyStatus = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<VerifyStatus>('loading');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    let isActive = true;

    async function verify() {
      if (!token) {
        setStatus('error');
        setMessage('Verification token is missing.');
        return;
      }

      try {
        const response = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
          credentials: 'include',
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.error || 'Verification failed.');
        }

        if (!isActive) return;
        setStatus('success');
        setMessage(data?.data?.message || 'Email verified. You can now sign in.');
      } catch (error) {
        if (!isActive) return;
        const errorMessage = error instanceof Error ? error.message : 'Verification failed.';
        setStatus('error');
        setMessage(errorMessage);
      }
    }

    verify();

    return () => {
      isActive = false;
    };
  }, [token]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-color, #f9fafb)',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: 'var(--card-bg, #ffffff)',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: 'var(--text-color, #1f2937)',
            marginBottom: '12px',
          }}
        >
          Email Verification
        </h1>

        <p
          style={{
            color: 'var(--text-muted, #6b7280)',
            fontSize: '14px',
            marginBottom: '20px',
          }}
        >
          {status === 'loading' ? 'Please wait while we verify your email.' : 'You can continue once verified.'}
        </p>

        {status === 'success' && (
          <div style={{ marginBottom: '16px' }}>
            <SuccessMessage message={message} />
          </div>
        )}

        {status === 'error' && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage message={message} />
          </div>
        )}

        <Button
          variant="primary"
          onClick={() => navigate('/signin')}
          disabled={status === 'loading'}
        >
          Go to sign in
        </Button>
      </div>
    </div>
  );
}
